import React, { ReactElement, useCallback, useMemo } from "react";

import { endOfDay } from "date-fns";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";

import useCheckPermissions from "@fartherfinance/frontend/api/Authz/hooks/useCheckPermission";
import useAdvisorClientsV2 from "@fartherfinance/frontend/api/Entity/hooks/useAdvisorClientsV2";
import useGetAdvisorsForBookAnalytics from "@fartherfinance/frontend/api/Entity/hooks/useGetAdvisorsForBookAnalytics";
import usePostHousehold from "@fartherfinance/frontend/api/Households/hooks/usePostHousehold";
import { CreateHouseholdRequestBody } from "@fartherfinance/frontend/api/Households/types";
import { AdvisorId, ClientId } from "@fartherfinance/frontend/api/Types";

import {
  HouseholdAdvisorFormField,
  HouseholdEstablishedAtFormField,
  HouseholdMemberFormField,
  HouseholdNameFormField,
} from "../HouseholdFormFields";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import { useAdvisorPageInit } from "@src/multiCustodian/hooks/Advisor/useAdvisorPageInit";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { EnhancedAutocompleteOption } from "@src/multiCustodian/pages/Advisor/common";
import { formatPersonName } from "@src/multiCustodian/pages/Advisor/utils";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import { handleApiError } from "@src/utils/handleApiError";

import styles from "./CreateHouseholdsForm.module.css";

interface Form {
  householdName: string;
  ownerClientId: EnhancedAutocompleteOption<ClientId>;
  advisorId: EnhancedAutocompleteOption<AdvisorId>;
  establishedAt?: Date | null;
}

interface Props {
  onCancel: () => void;
  onSuccess: () => void;
}

const formFieldRules = {
  ownerClientId: {
    required: "Point of Contact is required",
  },
};

const CreateHouseholdForm: React.FC<Props> = ({
  onCancel,
  onSuccess,
}: Props): ReactElement => {
  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      householdName: "",
      ownerClientId: undefined,
      advisorId: undefined,
      establishedAt: null,
    },
  });

  const {
    watch,
    getValues,
    formState: { isValid, isDirty },
  } = methods;

  const { auth, advisorId } = useAdvisorPageInit();

  const navigate = useNavigate();
  const location = useLocation();

  const notify = useStatusNotification();

  const selectedAdvisorId = watch("advisorId")?.value || null;

  const canViewDropdownResponse = useCheckPermissions(
    {
      type: "Frontend",
      action: "householding::view::cxFlow::advisorDropdown",
      id: "Webapp",
    },
    auth
  );

  const canViewDropdown = useMemo(() => {
    return canViewDropdownResponse?.data?.authorized;
  }, [canViewDropdownResponse?.data?.authorized]);

  const householdAdvisorId = useMemo(() => {
    if (canViewDropdown) {
      return selectedAdvisorId;
    }

    return advisorId !== undefined ? advisorId : null;
  }, [selectedAdvisorId, advisorId, canViewDropdown]);

  const { data: employees, isLoading: isLoadingEmployees } =
    useGetAdvisorsForBookAnalytics(auth);

  const { data: advisorClientsResponseData, isLoading: isLoadingClients } =
    useAdvisorClientsV2(householdAdvisorId, auth);

  const { mutateAsync: postHousehold, isLoading: isMutatingHousehold } =
    usePostHousehold(auth);

  const advisorOptions = useMemo(() => {
    if (!employees) {
      return [];
    }
    return employees.map((employee) => ({
      label: formatPersonName(employee.name),
      value: employee.advisorId,
    }));
  }, [employees]);

  const clientsOptions = useMemo(() => {
    if (!advisorClientsResponseData) {
      return [];
    }
    return advisorClientsResponseData.clients.map((client) => ({
      label: formatPersonName(client.name),
      value: client.clientId,
    }));
  }, [advisorClientsResponseData]);

  const openHousehold = useCallback(
    (householdId) => {
      navigate({
        ...location,
        pathname: `/Advisor/Households/${householdId}/Profile`,
      });
    },
    [navigate, location]
  );

  const handleOnSave = async () => {
    if (!isValid || householdAdvisorId === null) {
      return;
    }

    const values = getValues();

    try {
      const requestBody: CreateHouseholdRequestBody = {
        advisorId: householdAdvisorId,
        name: values.householdName,
        members: [
          {
            clientId: values.ownerClientId.value,
            memberType: "Owner",
            pointOfContact: true,
          },
        ],
        ...(values.establishedAt && {
          establishedAt: endOfDay(values.establishedAt).toISOString(),
        }),
      };

      const response = await postHousehold(
        { body: requestBody },
        {
          onSuccess: () => {
            onSuccess();
            notify("Household created successfully", "Success");
          },
        }
      );

      // Track household creation
      trackEvent({
        name: "Advisor Create Household",
        properties: {
          householdId: response.data.householdId,
          householdName: values.householdName,
          advisorId: householdAdvisorId,
          ownerClientId: values.ownerClientId.value,
        },
      });

      openHousehold(response.data.householdId);
    } catch (error) {
      handleApiError({
        error,
        errorMessage: "Failed to create household:",
        genericErrorMessage:
          "Something went wrong while creating the household. Please try again.",
        notify,
      });
    }
  };

  const isDisabled = useMemo(
    () => isMutatingHousehold || !isValid || !isDirty,
    [isMutatingHousehold, isValid, isDirty]
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleOnSave)}>
        {canViewDropdown && (
          <div className={styles.formField}>
            <HouseholdAdvisorFormField
              name="advisorId"
              label="Advisor"
              advisorOptions={advisorOptions}
              isLoading={isLoadingEmployees}
            />
          </div>
        )}
        <div className={styles.formField}>
          <HouseholdNameFormField />
        </div>
        {householdAdvisorId && (
          <div className={styles.formField}>
            <HouseholdMemberFormField
              name="ownerClientId"
              label="Point of Contact"
              rules={formFieldRules.ownerClientId}
              clientsOptions={clientsOptions}
              isLoading={isLoadingClients}
            />
          </div>
        )}
        <div className={styles.formField}>
          <HouseholdEstablishedAtFormField />
        </div>
        <div className={styles.footer}>
          <Button
            text="Cancel"
            onClick={onCancel}
            variant="outlined"
            buttonType="primary"
          />

          <Button
            text="Create Household"
            variant="contained"
            buttonType="primary"
            onClick={handleOnSave}
            disabled={isDisabled}
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default CreateHouseholdForm;
